// voci-api.js — amm-v3 (A4, ondata 31): parte PURA della vista "Voci &
// trasferte" (pagina 'voci' — aggregato categoria×dipendente×mese sulle voci
// del cedolino, A2-001/A2-020), testabile sotto node (contract test
// server/src/lib/amm-v3-voci.test.js).
//
// PRINCIPI (rito A1/A2/A3/proc-v3):
//  - TESTI VERBATIM dal v1 (admin-module.js, renderVoci r.2720-2833): pinnati
//    dal contract test.
//  - Le FORMULE della tabella replicate come funzioni pure — in particolare il
//    RAGGRUPPAMENTO per categoria nell'ordine fisso ORDINE_CAT (r.2746-2754,
//    NON l'ordine d'arrivo) e i totali di riga-categoria (r.2764-2772).
//  - LABEL_CATEGORIA_VOCE duplicato dal v1 (r.2734-2738: enum fisso di 11
//    valori, il client non fa una chiamata di rete per un enum) — pinnato.
//  - STATO CONDIVISO col v1: anno su AMM.vociAnno (r.751), categoria su
//    AMM.vociCategoria (r.755) — flip di flag senza perdite.
//  - DELEGHE: classificaEsistenti (POST), export CSV, fmtEur2, toast.
//    ZERO bypass di render (censiti: 3 handler interni prev/next/chip +
//    classifica, tutti dal DOM v1 che nel mondo v3 non esiste) ⇒ NIENTE
//    interceptor (come A3).
//  - MAI 'nav:badge' o canali nuovi da client/src/amm-v3/**.

import { apiPathFn } from './api.js';

export const AV_DELEGATED_METHODS = [
  'renderPage',   // il seam stesso (wrap) + refresh post-azione
  'renderVoci',   // la vista v1 wrappata (drift guard: rename ⇒ rosso)
  'fmtEur2',      // € a 2 decimali (unica fonte: v1, r.6855)
  'toast',        // esito classifica/export (stesso canale del v1)
];

export const AV_API_PATHS = [
  'voci.aggregato',           // GET /voci/aggregato?anno=[&categoria=]
  'voci.classificaEsistenti', // POST /voci/classifica-esistenti
  'export.download',          // Esporta CSV (vista 'voci')
];

// ---- TESTI VERBATIM dal v1 (pinnati dal contract test) ----
export const AV_TEXTS = {
  title: 'Voci & trasferte',
  sub: 'trasferte e altre voci del cedolino, per categoria e dipendente',
  classifica: 'Classifica voci esistenti',
  classificaBusy: 'Classificazione…',
  exportCsv: '+ Esporta CSV',
  kpiTotale: 'Totale ',            // + anno
  kpiCategorie: 'Categorie con movimenti',
  colDipendente: 'Dipendente',
  colTotale: 'Totale',
  totalePre: 'TOTALE ',            // + labelCat
  emptyPre: 'Nessuna voce classificata nel ',
  emptyCatPre: ' per la categoria "',
  emptyCatPost: '"',
  emptyPost: '.',
  emptyHint: ' Se i cedolini hanno già delle voci, prova "Classifica voci esistenti".',
  chipTutte: 'Tutte le categorie',
  toastClassificatePost: ' voci classificate',
  toastClassificaFallita: 'Classificazione fallita: ',
  toastExportFallito: 'Export fallito: ',
};

// mesi brevi capitalizzati — VERBATIM v1 r.2722 (intestazioni colonne)
export const MESI_S = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];

// LABEL_CATEGORIA_VOCE — VERBATIM v1 r.2734-2738 (enum fisso di 11 valori)
export const LABEL_CAT = {
  trasferta: 'Trasferta', rimborso_km: 'Rimborso km', straordinario: 'Straordinario',
  evr: 'EVR', ape_gnf: 'APE/GNF', buoni_pasto: 'Buoni pasto', fringe_benefit: 'Fringe benefit',
  cassa_edile: 'Cassa edile', indennita: 'Indennità', anticipo: 'Anticipo', altro: 'Altro',
};
// ordine fisso delle categorie (r.2740) e chips (r.2787: '' = Tutte, davanti)
export const ORDINE_CAT = ['trasferta', 'rimborso_km', 'straordinario', 'evr', 'ape_gnf', 'buoni_pasto', 'fringe_benefit', 'cassa_edile', 'indennita', 'anticipo', 'altro'];
export const CHIP_CATEGORIE = ['', 'trasferta', 'rimborso_km', 'straordinario', 'evr', 'ape_gnf', 'buoni_pasto', 'fringe_benefit', 'cassa_edile', 'indennita', 'anticipo', 'altro'];

export function labelCat(c) { return LABEL_CAT[c] || c; }

// nome dipendente della riga (r.2757): "cognome nome" o fallback dipendente_id
export function nomeRiga(r) {
  return [r.cognome, r.nome].filter(Boolean).join(' ') || r.dipendente_id;
}

// ---- STATO condiviso col v1 ----
export function vociStato(A) {
  if (!A.vociAnno) A.vociAnno = new Date().getFullYear();
  return { anno: A.vociAnno, categoria: A.vociCategoria || '' };
}

// ---- FORMULA v1 replicata (pura): raggruppamento per categoria in ORDINE_CAT
// (r.2746-2754) — le righe arrivano ordinate per dipendente e mescolerebbero
// le categorie; qui si raggruppano nell'ordine fisso, con le categorie fuori
// catalogo (mai, oggi) accodate. Restituisce solo i gruppi non vuoti (r.2754).
export function raggruppaVoci(righe) {
  const gruppi = ORDINE_CAT.map((c) => ({ categoria: c, righe: [] }));
  const mappa = {};
  gruppi.forEach((g) => { mappa[g.categoria] = g; });
  (righe || []).forEach((r) => {
    let g = mappa[r.categoria];
    if (!g) { g = { categoria: r.categoria, righe: [] }; mappa[r.categoria] = g; gruppi.push(g); }
    g.righe.push(r);
  });
  return gruppi.filter((g) => g.righe.length);
}

// totali per mese di un gruppo-categoria (r.2765-2766): somma delle 12 celle
export function totaliMesiCategoria(gruppo) {
  const tot = new Array(12).fill(0);
  (gruppo.righe || []).forEach((r) => { (r.mesi || []).forEach((m, i) => { tot[i] += Number(m) || 0; }); });
  return tot;
}

// ---- testi calcolati (r.2803-2810) ----
export function emptyMessage(anno, categoria) {
  return AV_TEXTS.emptyPre + anno
    + (categoria ? AV_TEXTS.emptyCatPre + labelCat(categoria) + AV_TEXTS.emptyCatPost : '')
    + AV_TEXTS.emptyPost
    + (categoria ? '' : AV_TEXTS.emptyHint);
}
export function chipLabel(c) { return c === '' ? AV_TEXTS.chipTutte : labelCat(c); }
export function classificateToast(res) { return (res && res.classificate != null ? res.classificate : 0) + AV_TEXTS.toastClassificatePost; }

// ---- LETTURA dati (default v1 r.2726-2728) ----
export async function readVoci(win, anno, categoria) {
  const d = await apiPathFn(win, 'voci.aggregato').call(null, anno, categoria || undefined);
  const dati = d || { righe: [], totali_per_categoria: {}, totale: 0 };
  return {
    righe: dati.righe || [],
    totali_per_categoria: dati.totali_per_categoria || {},
    totale: dati.totale || 0,
  };
}

// ---- AZIONI e DELEGHE (stessi punti del v1) ----
export function classificaEsistenti(win) { return apiPathFn(win, 'voci.classificaEsistenti').call(null); }
export function esportaCsvVoci(win, anno, categoria) {
  return apiPathFn(win, 'export.download').call(null, 'voci', anno, categoria ? { categoria } : undefined);
}
